import React, { act, useEffect, useState } from "react";
import {
  Table,
  Tag,
  Typography,
  Card,
  Avatar,
  Spin,
  Tooltip,
  Pagination,
  Tabs,
  Descriptions,
} from "antd";
import { useMediaQuery } from "react-responsive";
import paymentOperationsService from "../../services/paymentOperationsService";
import FeedBackEvalErrors from "../../page-components/FeedBackEvalErrors";
import usePermissions from "../../hooks/usePermissions";

const { Title } = Typography;
const { TabPane } = Tabs;

const RefrencesErrors = () => {
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const { hasPermission } = usePermissions()

  const [data, setData] = useState([]);
  const [feedBackData, setFeedBackData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("1");

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: false,
  });

  const fetchData = async (page, pageSize) => {
    setLoading(true);
    try {
      const response = await paymentOperationsService.getRefrencesErrorData(page, pageSize);
      if (response.success) {
        setData(response?.data?.results?.data || []);
        setPagination({
          ...pagination,
          total: response?.data?.count || 0,
          current: page,
          pageSize,
        });
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeedBackData = async (page, pageSize) => {
    setLoading(true);
    try {
      const response = await paymentOperationsService.getFeedbackEvalData(page, pageSize);
      if (response.success) {
        setFeedBackData(response?.data?.results?.data || []);
        setPagination({
          ...pagination,
          total: response?.data?.count || 0,
          current: page,
          pageSize,
        });
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasPermission("can_view_flagged_submission_reference_style")) {
      setActiveTab("1");
    } else if (hasPermission("can_view_flagged_submission_feedback")) {
      setActiveTab("2");
    }
  }, []);

  useEffect(() => {
    if (activeTab === "1" && hasPermission("can_view_flagged_submission_reference_style")) {
      fetchData(pagination.current, pagination.pageSize);
    }
    else if (activeTab === "2" && hasPermission("can_view_flagged_submission_feedback")) {
      fetchFeedBackData(pagination.current, pagination.pageSize);
    }
  }, [activeTab, pagination.current, pagination.pageSize]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    setPagination({
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: false,
    });
  };

  const columns = [
    {
      title: "Order ID",
      dataIndex: "task_id",
      key: "task_id",
      render: (text) => (
        <a href={`/order-management/my-orders/${text}`} target="_blank" rel="noopener noreferrer">
          {text}
        </a>
      ),
    },
    {
      title: "Major Issues",
      dataIndex: "major_count",
      key: "major_count",
      render: (count) => <Tag color="red">{count}</Tag>,
    },
    {
      title: "Minor Issues",
      dataIndex: "minor_count",
      key: "minor_count",
      render: (count) => <Tag color="orange">{count}</Tag>,
    },
    {
      title: "Forwarded to QC By",
      dataIndex: "forwarded_to_qc_by",
      key: "forwarded_to_qc_by",
      render: (arr) =>
        arr.length > 0 ? (
          <Avatar.Group maxCount={4}>
            {arr.map((user, idx) => (
              <Tooltip title={user} key={idx}>
                <Avatar style={{ backgroundColor: "#87d068" }}>{user?.charAt(0).toUpperCase()}</Avatar>
              </Tooltip>
            ))}
          </Avatar.Group>
        ) : (
          <Tag color="gray">None</Tag>
        ),
    },
  ];

  return (
    <div style={{ margin: 10, padding: 24, background: "#fff", borderRadius: 8 }}>
      <Tabs defaultActiveKey="1" onChange={handleTabChange}>
        {hasPermission("can_view_flagged_submission_reference_style") &&
          <TabPane tab="Refrences Errors" key="1">
            {isMobile ? (
              <Spin spinning={loading} tip="Loading..">
                <MobileView
                  data={data}
                  pagination={pagination}
                  setPagination={setPagination}
                />
              </Spin>
            ) : (
              <Table
                dataSource={data}
                columns={columns}
                rowKey="task_id"
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: pagination.total,
                  onChange: (page, pageSize) => {
                    setPagination({ ...pagination, current: page, pageSize });
                  },
                  onShowSizeChange: (current, size) => {
                    setPagination({ ...pagination, current: 1, pageSize: size });
                  },
                }}
                bordered
                loading={loading}
              />
            )}
          </TabPane>
        }
        {hasPermission("can_view_flagged_submission_feedback") &&
          <TabPane tab="Feedback Errors" key="2">
            <FeedBackEvalErrors
              feedBackData={feedBackData}
              loading={loading}
              pagination={pagination}
              setPagination={setPagination}
              fetchFeedBackData={fetchFeedBackData}
            />
          </TabPane>
        }
      </Tabs>
    </div>
  );
};

const MobileView = ({ data, pagination, setPagination }) => (
  <>
    {data.map((item) => (
      <Card
        key={item.task_id}
        style={{ marginBottom: 16, borderRadius: 8 }}
        bodyStyle={{ padding: 0 }}
        type="inner"
      >
        <Descriptions
          column={1}
          size="small"
          bordered
          style={{ margin: 0 }}
        >
          <Descriptions.Item label="Order ID">
            <a
              href={`/order-management/my-orders/${item.task_id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {item.task_id}
            </a>
          </Descriptions.Item>

          <Descriptions.Item label="Major Issues">
            <Tag color="red">{item.major_count}</Tag>
          </Descriptions.Item>

          <Descriptions.Item label="Minor Issues">
            <Tag color="orange">{item.minor_count}</Tag>
          </Descriptions.Item>

          <Descriptions.Item label="Forwarded to QC By">
            {item.forwarded_to_qc_by.length > 0 ? (
              <Avatar.Group maxCount={4}>
                {item.forwarded_to_qc_by.map((user, idx) => (
                  <Tooltip title={user} key={idx}>
                    <Avatar style={{ backgroundColor: "#87d068" }}>
                      {user?.charAt(0).toUpperCase()}
                    </Avatar>
                  </Tooltip>
                ))}
              </Avatar.Group>
            ) : (
              <Tag color="gray">None</Tag>
            )}
          </Descriptions.Item>
        </Descriptions>
      </Card>
    ))}

    <div style={{ textAlign: "center", marginTop: 16 }}>
      <Pagination
        current={pagination.current}
        pageSize={pagination.pageSize}
        total={pagination.total}
        onChange={(page) => setPagination((prev) => ({ ...prev, current: page }))}
        showSizeChanger={false}
      />
    </div>
  </>
);

export default RefrencesErrors;
